import {
	Grid,
	HStack,
	Text,
	VStack
} from '@chakra-ui/react';

import { lang } from '@lang';
import { RtaFeatureButton, PassoffModal, QuoteVerificationModal } from '@components';
import { useTax } from '@hooks';
import { usePassoffFeature, BlueprintDisplayValues, HubSpotBlueprintFormState } from '@features/crm';

import type { JSX } from 'react';
import type { Note } from '@rta/rta-blueprint-library';
import type { Project } from '@state';
import type { VerifiedQuoteItem, VerifyQuoteItemsSummary } from '@external';

export const SlackActionPanel = (): JSX.Element => {
	const [tax] = useTax();

	const passoffFeature = usePassoffFeature();

	// Extract modal data from passoffFeature
	const passoffModalData = passoffFeature.modal?.[0]?.data as {
		checklist: { fuelSource: boolean; islandDimensions: boolean[]; countertopDimensions: boolean[]; utilityCutouts: boolean };
		projectFolder: string;
		cutoutOptions: string;
		additionalDetails: string;
		isSubmitting: boolean;
		isSubmitDisabled: boolean;
		allChecklistItemsChecked: boolean;
		willSubmitQuote: boolean;
		blueprintValues: BlueprintDisplayValues;
		comments: Note[];
		deals: Array<Project>;
		selectedDealId: string | null;
		handleDealSelect: (dealId: string) => void;
		startVerification: () => Promise<void>;
		updateChecklist: (key: 'fuelSource' | 'utilityCutouts', value: boolean) => void;
		updateChecklistArray: (key: 'islandDimensions' | 'countertopDimensions', index: number, value: boolean) => void;
		updateProjectFolder: (projectFolder: string) => void;
		updateCutoutOptions: (cutoutOptions: string) => void;
		updateAdditionalDetails: (additionalDetails: string) => void;
		submitPassoff: () => void;
		passoffStep: 'deal-selection' | 'verification' | 'checklist';
		isVerifying: boolean;
		verifiedItems: VerifiedQuoteItem[];
		verificationSummary: VerifyQuoteItemsSummary | null;
		continueToChecklist: () => void;
		backToVerification: () => void;
		handlePriceSelectionChange: (index: number, selection: 'hubspot' | 'tool') => void;
		quoteName: string;
		defaultQuoteName: string;
		handleQuoteNameChange: (name: string) => void;
		submitQuote: boolean;
		handleSubmitQuoteChange: (value: boolean) => void;
		hubspotForm: HubSpotBlueprintFormState;
		updateHubspotFormField: <K extends keyof HubSpotBlueprintFormState>(field: K, value: HubSpotBlueprintFormState[K]) => void;
	} | undefined;

	return (
		<>
			<Grid width="100%" height="100%" templateRows="auto 1fr">
				<HStack h="35px" w="full" background="purple.500" px={6} py={2} justifyContent={'center'} mb={2}>
					<Text textAlign={'center'} color={'white'} fontSize={18}>{lang.actionPanels.title.slack}</Text>
				</HStack>
				<VStack width="165px" spacing={2} mx="5px" border={'0px'} >
					<RtaFeatureButton feature={passoffFeature} useIcon/>
				</VStack>
			</Grid>

			{/* Deal Selection + Quote Verification Modal */}
			{passoffFeature.modal && passoffModalData && (passoffModalData.passoffStep === 'deal-selection' || passoffModalData.passoffStep === 'verification') && (
				<QuoteVerificationModal
					isOpen={passoffFeature.modal[0].isOpen}
					onClose={passoffFeature.modal[1]}
					isLoading={passoffModalData.isVerifying}
					verifiedItems={passoffModalData.verifiedItems}
					summary={passoffModalData.verificationSummary}
					onConfirm={passoffModalData.continueToChecklist}
					onCancel={passoffFeature.modal[1]}
					isSubmitting={false}
					onPriceSelectionChange={passoffModalData.handlePriceSelectionChange}
					confirmButtonText="Continue to Checklist"
					confirmButtonTextWithIssues="Continue to Checklist"
					quoteName={passoffModalData.quoteName}
					onQuoteNameChange={passoffModalData.handleQuoteNameChange}
					step={passoffModalData.passoffStep === 'deal-selection' ? 'deal-selection' : 'verification'}
					deals={passoffModalData.deals}
					selectedDealId={passoffModalData.selectedDealId}
					onDealSelect={passoffModalData.handleDealSelect}
					onContinueToverification={passoffModalData.startVerification}
				/>
			)}

			{/* Submit Blueprint Modal - shown after verification */}
			{passoffFeature.modal && passoffModalData && passoffModalData.passoffStep === 'checklist' && (
				<PassoffModal
					isOpen={passoffFeature.modal[0].isOpen}
					onClose={passoffFeature.modal[1]}
					checklist={passoffModalData.checklist}
					projectFolder={passoffModalData.projectFolder}
					cutoutOptions={passoffModalData.cutoutOptions}
					additionalDetails={passoffModalData.additionalDetails}
					isSubmitting={passoffModalData.isSubmitting}
					isSubmitDisabled={passoffModalData.isSubmitDisabled}
					allChecklistItemsChecked={passoffModalData.allChecklistItemsChecked}
					willSubmitQuote={passoffModalData.willSubmitQuote}
					blueprintValues={passoffModalData.blueprintValues}
					comments={passoffModalData.comments}
					updateChecklist={passoffModalData.updateChecklist}
					updateChecklistArray={passoffModalData.updateChecklistArray}
					updateProjectFolder={passoffModalData.updateProjectFolder}
					updateCutoutOptions={passoffModalData.updateCutoutOptions}
					updateAdditionalDetails={passoffModalData.updateAdditionalDetails}
					submitPassoff={passoffModalData.submitPassoff}
					submitQuote={passoffModalData.submitQuote}
					onSubmitQuoteChange={passoffModalData.handleSubmitQuoteChange}
					hubspotForm={passoffModalData.hubspotForm}
					updateHubspotFormField={passoffModalData.updateHubspotFormField}
				/>
			)}
		</>
	);
};
